import { ApplianceCommand, ApplianceCommandErrorResult, ApplianceCommandSuccessResult, Message } from './types'
import { canonicalize } from './canonicalize'
import { RsInput, RsOutput } from './messages'
import { jsonParse, jsonStringify } from './serialization'

export type Optional<T, K extends keyof T> = { [P in Exclude<keyof T, K>]: T[P] } & { [P in keyof Pick<T, K>]+?: T[P] }
export type Nullable<T> = T | null
export type Required<T, K extends keyof T> = { [P in K]-?: T[P] } & { [P in Exclude<keyof T, K>]: T[P] }
export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] }

// Omits a property from every union type (thanks ChatGPT).
export type OmitFromUnion<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never
export type KeysOfUnion<T> = T extends T ? keyof T : never
export type RequiredNotNull<T> = {
    [P in keyof T]: NonNullable<T[P]>
}
export type Ensure<T, K extends keyof T> = T & RequiredNotNull<Pick<T, K>>

export type RecursivePartial<T> = {
    [P in keyof T]?: T[P] extends (infer U)[]
        ? RecursivePartial<U>[]
        : T[P] extends object
        ? RecursivePartial<T[P]>
        : T[P]
}

export function compareSimple<T extends string | number>(t1: T, t2: T) {
    if (t1 < t2) {
        return -1
    }
    if (t1 > t2) {
        return 1
    }
    return 0
}
export function compare<T>(t1: T, t2: T, map: (t: T) => (string | number)[]): number {
    const v1 = map(t1)
    const v2 = map(t2)
    for (let i = 0; i < v1.length; i++) {
        const r = compareSimple(v1[i], v2[i])
        if (r !== 0) {
            return r
        }
    }
    return 0
}
export function sort<T>(...k: (keyof T | ((v: T) => string | number))[]): (t1: T, t2: T) => number {
    type P = T[keyof T] extends number | string ? T[keyof T] : never
    return (t1: T, t2: T) =>
        compare(t1, t2, (t) =>
            k.map((keyOrSelector) => (typeof keyOrSelector !== 'function' ? (t[keyOrSelector] as P) : keyOrSelector(t)))
        )
}

export const sortByStreamId = (a: RsInput | RsOutput, b: RsInput | RsOutput) => {
    if (a.streamId === b.streamId) return 0
    if (a.streamId > b.streamId) return 1
    return -1
}

// Credit: https://github.com/ide/backoff
// MIT License

// Copyright (c) 2019 James Ide

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
export function computeNextBackoffInterval(
    initialBackoff: number,
    previousRetryCount: number,
    { multiplier = 1.5, randomizationFactor = 0.25, minBackoff = initialBackoff, maxBackoff = Infinity } = {}
): number {
    assert(initialBackoff > 0, `The initial backoff interval must be positive`)
    assert(previousRetryCount >= 0, `The previous retry count must not be negative`)
    assert(multiplier >= 1, `The backoff multiplier must be greater than or equal to 1`)
    assert(
        0 <= randomizationFactor && randomizationFactor <= 1,
        `The randomization factor must be between 0 and 1, inclusive`
    )
    assert(minBackoff >= 0, `The minimum backoff interval must be positive`)

    const nextBackoff = initialBackoff * multiplier ** previousRetryCount
    // Apply jitter within the negative to positive range of the randomization factor
    const jitterFactor = 1 - randomizationFactor + 2 * randomizationFactor * Math.random()
    return Math.min(Math.max(nextBackoff * jitterFactor, minBackoff), maxBackoff)
}

function assert(cond: any, message: string) {
    if (!cond) {
        throw new Error(message)
    }
}

export const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms))

export function withTimeout<T>(timeoutMs: number, fn: () => Promise<T>) {
    return () => {
        const timeout = sleep(timeoutMs).then(() => Promise.reject(new Error('Timeout')))
        return Promise.race([fn(), timeout])
    }
}

export async function orTimeout<T>(p: Promise<T>, timeoutMs: number) {
    return await Promise.race([
        p,
        sleep(timeoutMs).then(() => {
            throw new Error(`Timeout ${timeoutMs}ms`)
        }),
    ])
}

export async function retry<T>(
    op: () => Promise<T>,
    opt: {
        initialBackoff: number
        maxRetries: number
        maxBackoff: number
    }
) {
    const { maxRetries, initialBackoff, maxBackoff } = opt
    let lastError: any
    for (let i = 0; i <= maxRetries; i++) {
        try {
            return await op()
        } catch (e) {
            lastError = e
            if (i < maxRetries) {
                const nextSleep = computeNextBackoffInterval(initialBackoff, i, { maxBackoff: maxBackoff | Infinity })
                await sleep(nextSleep)
                continue
            }
        }
    }
    throw lastError
}

export function equalsMap<T, U extends string | number>(map: (v: T) => U) {
    return (t1: T, t2: T) => {
        return map(t1) === map(t2)
    }
}

export function ageSeconds(d: Date, now: Date = new Date()) {
    return Math.floor((now.valueOf() - d.valueOf()) / 1000)
}

export class Deferred<T> {
    promise: Promise<T>
    resolve!: (obj?: any) => void
    reject!: (err?: any) => void

    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }
}

export async function awaitCondition(
    cond: () => Promise<boolean>,
    timeoutMs: number,
    intervalMs: number,
    throwOnError: boolean = false
) {
    let v: boolean = false
    const start = Date.now()

    function maybeThrow(p: Promise<boolean>): typeof p {
        return p.catch((e) => {
            if (throwOnError) {
                throw e
            }
            return false
        })
    }

    while (!(v = await maybeThrow(cond())) && Date.now() - start < timeoutMs) {
        await sleep(intervalMs)
    }
    return v
}

export function isValidUuid(uuid: unknown, version?: 1 | 2 | 3 | 4 | 5): boolean {
    const uuidRegex = version
        ? new RegExp(`^[0-9a-f]{8}-[0-9a-f]{4}-${version}[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$`, 'i')
        : /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    return uuidRegex.test(uuid as string)
}

/** @deprecated Use isValidUuid instead. isValidUuidV4 also allows uuid v1-v5. */
export function isValidUuidV4(uuid: unknown): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    return uuidRegex.test(uuid as string)
}

export function clone<T>(t: T): T {
    return jsonParse(jsonStringify(t))
}

export function omitProperties<T, K extends Array<keyof T>>(t: T, ...k: K) {
    const c = clone(t)
    for (const p of k) {
        delete c[p]
    }
    return c as Omit<T, K[number]>
}

export function toBooleanString(b: boolean) {
    if (b) {
        return 'yes'
    }
    return 'no'
}

export function applianceCommandSuccessResult(
    command: ApplianceCommand,
    result: any
): Message<ApplianceCommandSuccessResult, 'commandResult'> {
    return {
        type: 'commandResult',
        data: {
            success: true,
            result: result || {},
            command,
        },
    }
}

export function applianceCommandErrorResult(
    command: ApplianceCommand,
    errorMessage: string
): Message<ApplianceCommandErrorResult, 'commandResult'> {
    return {
        type: 'commandResult',
        data: {
            success: false,
            errorMessage,
            command,
        },
    }
}

export const equals = <T>(objA: T, objB: T): boolean => {
    return canonicalize(objA) == canonicalize(objB)
}

export function pick<T, K extends keyof T>(t: T, ...pickKeys: K[]) {
    if (!t) {
        return t
    }
    const o: Partial<Pick<T, K>> = {}
    for (const k of pickKeys) {
        o[k] = t[k]
    }
    return o as Pick<T, K>
}

export function assertExclude<T extends string, U extends T>(value: T, excluded: U, errorMessage?: string) {
    if (value == excluded) {
        throw new Error(errorMessage || `Unexpected value: ${excluded}`)
    }
    return value as Exclude<T, U>
}

/**
 * Return pairs with one item from each of the sequences
 *
 * @param itemsA itemsA[i] == result[i][0]
 * @param itemsB itemsB[i] == result[i][1]
 */
export function pairs<A, B>(itemsA: A[], itemsB: B[]): Array<[A, B]> {
    if (itemsA.length !== itemsB.length) {
        throw new Error(`Can only pair sequences of the same length (${itemsA.length} != ${itemsB.length})`)
    }
    const result: Array<[A, B]> = []
    for (let i = 0; i < itemsA.length; i++) {
        result.push([itemsA[i], itemsB[i]])
    }
    return result
}

export function max(nums: number[]) {
    let leader = Number.NEGATIVE_INFINITY
    for (const n of nums) {
        if (n > leader) {
            leader = n
        }
    }
    return leader
}

export const isValidDate = (date: Date): boolean =>
    Object.prototype.toString.call(date) === '[object Date]' && Number.isFinite(date.getTime())

export function omitNullValuesShallow<T extends Record<string, any>>(obj: T) {
    const returned: Partial<T> = {}
    for (const k of Object.keys(obj) as Array<keyof T>) {
        if (obj[k] !== null) {
            returned[k] = obj[k]
        }
    }
    return returned as NullToUndefinedShallow<T>
}

type NullToUndefinedShallow<T> = {
    [K in keyof T]: null extends T[K] ? Exclude<T[K], null> : T[K]
}

export const toSnakeCase = (input: string): string => {
    return input
        .split(/(?=[A-Z])/)
        .join('_')
        .toLowerCase()
}

export const notNull = <T extends number | bigint | string | object>(v: T | null): v is Exclude<T, null> => v !== null

export function sortObjectByKeys(unsorted: Record<string, any>): Record<string, any> {
    return Object.keys(unsorted)
        .sort()
        .reduce<Record<string, any>>((sorted, key) => {
            const value = unsorted[key]
            const isObject = !!(value && typeof value === 'object' && !Array.isArray(value))
            return { ...sorted, [key]: isObject ? sortObjectByKeys(value) : value }
        }, {})
}

export function areObjectsEqual(a: Record<string, any> | undefined, b: Record<string, any> | undefined) {
    if (a && b) {
        return JSON.stringify(sortObjectByKeys(a)) === JSON.stringify(sortObjectByKeys(b))
    }
    return !a && !b
}
